import { useCallback, useEffect, useState } from "react";

import MinusIcon from "atoms/icon/icons/ic_minus_small_ol.svg";
import PlusIcon from "atoms/icon/icons/plus.svg";
import { IconButton } from "atoms/icon-button/IconButton";

interface QuantityStepperProps {
  defaultValue?: number;
  maxValue?: number;
  onChange: (quantity: number, invalid: boolean) => void;
  disabled?: boolean;
}

export const QuantityStepper = ({
  defaultValue = 1,
  maxValue,
  onChange,
  disabled = false,
}: QuantityStepperProps) => {
  const [quantity, setQuantity] = useState(defaultValue);

  const handleIncrement = useCallback(() => {
    if (!maxValue || quantity < maxValue || disabled) {
      const newQuantity = quantity + 1;
      setQuantity(newQuantity);
    }
  }, [quantity, maxValue]);

  const handleDecrement = useCallback(() => {
    if (quantity > 1 || disabled) {
      const newQuantity = quantity - 1;
      setQuantity(newQuantity);
    }
  }, [quantity]);

  // Update invalid state and notify parent when quantity changes
  useEffect(() => {
    const isInvalid = maxValue ? quantity > maxValue : false;
    onChange(quantity, isInvalid);
  }, [quantity, maxValue, onChange]);

  const isDecrementDisabled = quantity <= 1 || disabled;
  const isIncrementDisabled = maxValue
    ? quantity >= maxValue || disabled
    : false;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex items-center gap-3">
        <IconButton
          svg={MinusIcon}
          size="xxsmall"
          style="solid"
          disabled={isDecrementDisabled}
          onClick={handleDecrement}
          data-testid="decrease-quantity"
        />

        <div className="min-w-11 text-center text-body1 text-primary">
          <div>{quantity}</div>
        </div>

        <IconButton
          svg={PlusIcon}
          size="xxsmall"
          style="solid"
          disabled={isIncrementDisabled}
          onClick={handleIncrement}
          data-testid="increase-quantity"
        />
      </div>
    </div>
  );
};
